'use client';

import { useState } from 'react';

const INITIAL_STATE = {
  age: 30,
  weight: 170,
  height: 70,
  gender: 'male\' as \'male\' | \'female',
  activity: 1.55,
  calorieDeficit: 500,
  calorieSurplus: 500,
};

const PRESETS = {
  conservative: {
    age: 40,
    weight: 150,
    height: 66,
    gender: 'female\' as \'male\' | \'female',
    activity: 1.375,
    calorieDeficit: 250,
    calorieSurplus: 250,
  },
  moderate: {
    age: 30,
    weight: 170,
    height: 70,
    gender: 'male\' as \'male\' | \'female',
    activity: 1.55,
    calorieDeficit: 500,
    calorieSurplus: 500,
  },
  aggressive: {
    age: 25,
    weight: 200,
    height: 74,
    gender: 'male\' as \'male\' | \'female',
    activity: 1.725,
    calorieDeficit: 750,
    calorieSurplus: 750,
  },
};

export default function CalculatorCalorie() {
  const [age, setAge] = useState(INITIAL_STATE.age);
  const [weight, setWeight] = useState(INITIAL_STATE.weight);
  const [height, setHeight] = useState(INITIAL_STATE.height);
  const [gender, setGender] = useState<'male' | 'female'>(INITIAL_STATE.gender);
  const [activity, setActivity] = useState(INITIAL_STATE.activity);
  const [calorieDeficit, setCalorieDeficit] = useState(INITIAL_STATE.calorieDeficit);
  const [calorieSurplus, setCalorieSurplus] = useState(INITIAL_STATE.calorieSurplus);
  const [isCalculating, setIsCalculating] = useState(false);

  const handleChange = (setter: (v: any) => void, value: any) => {
    setter(value);
    setIsCalculating(true);
    setTimeout(() => setIsCalculating(false), 800);
  };

  const weightKg = weight * 0.453592;
  const heightCm = height * 2.54;
  
  const bmr = gender === 'male'
    ? 10 * weightKg + 6.25 * heightCm - 5 * age + 5
    : 10 * weightKg + 6.25 * heightCm - 5 * age - 161;
  
  const tdee = bmr * activity;
  const loseWeight = tdee - calorieDeficit;
  const gainWeight = tdee + calorieSurplus;

  const handleReset = () => {
    setAge(INITIAL_STATE.age);
    setWeight(INITIAL_STATE.weight);
    setHeight(INITIAL_STATE.height);
    setGender(INITIAL_STATE.gender);
    setActivity(INITIAL_STATE.activity);
    setCalorieDeficit(INITIAL_STATE.calorieDeficit);
    setCalorieSurplus(INITIAL_STATE.calorieSurplus);
  };

  const handlePreset = (preset: keyof typeof PRESETS) => {
    const values = PRESETS[preset];
    setAge(values.age);
    setWeight(values.weight);
    setHeight(values.height);
    setGender(values.gender);
    setActivity(values.activity);
    setCalorieDeficit(values.calorieDeficit);
    setCalorieSurplus(values.calorieSurplus);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
        {isCalculating && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-emerald-700 font-semibold">Calculating...</span>
            </div>
          </div>
        )}

        {/* Preset and Reset Buttons */}
        <div className="mb-6 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handlePreset('conservative')}
              className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm transition-all border border-blue-200"
            >
              Conservative
            </button>
            <button
              onClick={() => handlePreset('moderate')}
              className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-semibold text-sm transition-all border border-indigo-200"
            >
              Moderate
            </button>
            <button
              onClick={() => handlePreset('aggressive')}
              className="px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg font-semibold text-sm transition-all border border-purple-200"
            >
              Aggressive
            </button>
          </div>
          <button
            onClick={handleReset}
            className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold text-sm transition-all border border-slate-300"
          >
            Reset to Default
          </button>
        </div>

        <div className="space-y-6 mb-8">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-3">Gender</label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' },
              ].map((g) => (
                <button
                  key={g.value}
                  onClick={() => handleChange(setGender, g.value)}
                  className={`py-3 rounded-lg font-semibold transition-all ${
                    gender === g.value
                      ? 'bg-emerald-500 text-white' :'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Age</label>
              <input
                type="number"
                value={age}
                onChange={(e) => handleChange(setAge, Number(e.target.value))}
                className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Weight (lbs)</label>
              <input
                type="number"
                value={weight}
                onChange={(e) => handleChange(setWeight, Number(e.target.value))}
                className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Height (in)</label>
              <input
                type="number"
                value={height}
                onChange={(e) => handleChange(setHeight, Number(e.target.value))}
                className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Activity Level</label>
            <select
              value={activity}
              onChange={(e) => handleChange(setActivity, Number(e.target.value))}
              className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
            >
              <option value={1.2}>Sedentary (little or no exercise)</option>
              <option value={1.375}>Lightly active (1-3 days/week)</option>
              <option value={1.55}>Moderately active (3-5 days/week)</option>
              <option value={1.725}>Very active (6-7 days/week)</option>
              <option value={1.9}>Extra active (athlete)</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Weight Loss Deficit (cal/day)</label>
              <input
                type="number"
                value={calorieDeficit}
                onChange={(e) => handleChange(setCalorieDeficit, Number(e.target.value))}
                min={0}
                max={1000}
                step={50}
                className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Weight Gain Surplus (cal/day)</label>
              <input
                type="number"
                value={calorieSurplus}
                onChange={(e) => handleChange(setCalorieSurplus, Number(e.target.value))}
                min={0}
                max={1000}
                step={50}
                className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-gradient-to-br from-red-500 via-orange-500 to-amber-600 rounded-xl p-6 text-white text-center">
            <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-2">Daily Calories (Maintain)</div>
            <div className="text-5xl font-bold">{Math.round(tdee)}</div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-xs text-slate-600 mb-1">Lose Weight</div>
              <div className="text-xl font-bold text-slate-900">{Math.round(loseWeight)} cal/day</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-xs text-slate-600 mb-1">Gain Weight</div>
              <div className="text-xl font-bold text-slate-900">{Math.round(gainWeight)} cal/day</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}